import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, ChevronRight, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { fmtDateTime } from "../lib/time";

const durationLabel = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const STATUS_BODY_KEYS = new Set([
  "as_planned", "forgotten", "missed", "not_eaten",
]);

export const careNotificationBody = (row, t) => {
  const detail = row.care_schedule_correction;
  // Older payloads have only the exact enum stored by the correction endpoint.
  // Category is never inferred from that body, the title, or the deep link.
  const legacyCorrection = row.entity_type === "today_item"
    && (row.event_type || row.kind) === "schedule_correction" ? row.body : null;
  const correction = detail?.correction || legacyCorrection;
  if (!STATUS_BODY_KEYS.has(correction)) return row.body;
  if (correction === "missed") {
    const categoryKeys = {
      meal: "notificationStatus_not_eaten", nutrition: "notificationStatus_not_eaten",
      supplement: "notificationStatus_forgotten", supplements: "notificationStatus_forgotten",
      training: "missed", workout: "missed",
      recovery: "gamepowerReason_recovery_missed", physio: "gamepowerReason_physio_missed",
    };
    return t(categoryKeys[detail?.category] || "notificationStatus_missed");
  }
  return t(`notificationStatus_${correction}`);
};

export function CareNotificationRow({ row, onOpen, timezone, lang }) {
  const { t } = useI18n();
  const detail = row.care_request_preview;
  const topic = detail?.topic;
  const topicName = topic?.label || (topic?.category ? t(`requestCategory_${topic.category}`) : "");
  const topicLabel = topic?.state === "suggested" ? `${t("notificationSuggestion")}: ${topicName}`
    : topic?.state === "uncategorized" ? t("notificationUncategorized") : topicName;
  const preview = detail?.preview;
  const previewText = preview?.text ? `“${preview.text}”`
    : preview?.kind === "audio" ? `${t("voiceMessage")}${preview.duration_seconds != null ? ` · ${durationLabel(preview.duration_seconds)}` : ""}`
      : preview?.kind === "photo" ? t("photo") : preview?.kind === "file" ? t("file") : row.body;
  return <button type="button" onClick={() => onOpen(row)} className="w-full px-5 py-4 text-left flex gap-3" style={{ background: row.read_at ? "transparent" : "linear-gradient(90deg, var(--pc-cyan-dim), transparent)", color: "var(--pc-text)", borderColor: "var(--pc-border)" }} data-testid={`care-notification-${row.id}`}>
    <span className="relative w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--pc-card-inset)", color: row.read_at ? "var(--pc-muted)" : "var(--pc-cyan)" }}><Bell size={17} />{!row.read_at && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full" style={{ background: "var(--pc-cyan)" }} />}</span>
    <span className="min-w-0 flex-1">
      {detail ? <>
        <span className="flex gap-3"><span className="font-semibold text-sm truncate" data-testid="care-notification-player">{detail.player.name}</span><span className="ml-auto text-[10px] shrink-0" style={{ color: row.read_at ? "var(--pc-muted)" : "var(--pc-cyan)" }}>{t(row.read_at ? "notificationRead" : "notificationUnread")}</span></span>
        <span className="block text-xs font-medium mt-1" style={{ color: "var(--pc-cyan)" }} data-testid="care-notification-topic">{t(detail.event_key)}{topicLabel ? ` · ${topicLabel}` : ""}</span>
        <span className="block text-sm mt-1 line-clamp-2" style={{ color: "var(--pc-muted)" }} data-testid="care-notification-preview">{previewText}</span>
      </> : <>
        <span className="flex gap-3"><span className="font-semibold text-sm truncate">{row.title}</span><span className="ml-auto text-[10px] shrink-0" style={{ color: row.read_at ? "var(--pc-muted)" : "var(--pc-cyan)" }}>{t(row.read_at ? "notificationRead" : "notificationUnread")}</span></span>
        <span className="block text-sm mt-1 line-clamp-2" style={{ color: "var(--pc-muted)" }}>{careNotificationBody(row, t)}</span>
      </>}
      <span className="block text-xs mt-2" style={{ color: "var(--pc-muted)" }}>{fmtDateTime(row.created_at, timezone, lang)}</span>
    </span>
    <ChevronRight size={16} className="self-center shrink-0" color="var(--pc-muted)" />
  </button>;
}

export default function CareNotificationInbox({ onNavigate, onClose, fallbackPath = "/care", compact = false }) {
  const { user } = useAuth(); const { t, lang } = useI18n();
  const [rows, setRows] = useState([]); const [state, setState] = useState("loading");
  const snapshotRef = useRef([]); const markedRef = useRef(false);
  const load = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await api.get("/me/notifications");
      setRows(data.notifications || []);
      snapshotRef.current = (data.notifications || []).filter((row) => !row.read_at).map((row) => row.id);
      markedRef.current = false; setState("ready");
    } catch { setState("error"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  const markSnapshot = useCallback(async () => {
    if (markedRef.current) return;
    markedRef.current = true;
    try {
      const { data } = await api.post("/me/notifications/read-snapshot", {
        notification_ids: snapshotRef.current,
      });
      window.dispatchEvent(new CustomEvent("pc:notifications-read", {
        detail: { unreadCount: data.unread_count || 0 },
      }));
    } catch { markedRef.current = false; }
  }, []);
  useEffect(() => () => { markSnapshot(); }, [markSnapshot]);
  const open = async (row) => {
    await markSnapshot();
    onNavigate(row.deep_link && row.deep_link.startsWith("/") ? row.deep_link : fallbackPath);
  };
  return <section className={`pc-ops-panel overflow-hidden ${compact ? "flex max-h-[min(70vh,620px)] flex-col" : ""}`} data-testid="care-notification-inbox">
    <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--pc-border)" }}>
      <p className="text-sm min-w-0 flex-1" style={{ color: "var(--pc-muted)" }}>{t("notificationsHint")}</p>
      <button type="button" onClick={onClose} className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }} aria-label={t("close")} data-testid="care-notifications-close"><X size={16} /></button>
    </div>
    {state === "loading" && <div className="flex justify-center py-20"><div className="pc-spinner" /></div>}
    {state === "error" && <div className="p-6"><p>{t("todayError")}</p><button className="pc-btn pc-btn-ghost mt-3" onClick={load}>{t("retry")}</button></div>}
    {state === "ready" && rows.length === 0 && <div className="p-10 text-center"><Bell size={26} className="mx-auto" color="var(--pc-muted)" /><p className="text-sm mt-3" style={{ color: "var(--pc-muted)" }}>{t("noNotifications")}</p></div>}
    {state === "ready" && rows.length > 0 && <div className={`divide-y ${compact ? "min-h-0 overflow-y-auto pc-scroll" : ""}`} style={{ borderColor: "var(--pc-border)" }}>{rows.map((row) => <CareNotificationRow key={row.id} row={row} onOpen={open} timezone={user?.timezone} lang={lang} />)}</div>}
  </section>;
}
